import React from "react";
import { Link } from "react-router-dom";
import { WORKOUT } from "@/constants/testIds";
import { Plus, Trash2, Sparkles, Save, BookOpen, Repeat, PlayCircle } from "lucide-react";
import { kgToLb, lbToKg } from "@/lib/units";
import ExerciseIcon from "@/components/ExerciseIcon";
import NumField from "@/components/NumField";

const inputCls =
  "w-full bg-obsidian-900 border border-obsidian-600 px-2.5 py-2 rounded-sm focus:border-blaze focus:outline-none focus:ring-1 focus:ring-blaze text-sm";

// Display kg in the user's preferred unit. Storage stays in kg; the input
// just shows lbs when imperial. Round to nearest 0.5 to avoid float jitter
// from the round-trip kg → lb → kg.
const formatWeightInput = (kg, isImperial) => {
  if (kg === "" || kg == null) return "";
  const num = Number(kg);
  if (!Number.isFinite(num)) return "";
  if (!isImperial) return num;
  return Math.round(kgToLb(num) * 2) / 2;
};
const parseWeightInput = (val, isImperial) => {
  if (val === "" || val == null) return 0;
  const num = Number(val);
  if (!Number.isFinite(num)) return 0;
  return isImperial ? lbToKg(num) : num;
};

/**
 * Workout logger card — exposes the form for one session.
 * Inputs (sets, reps, weight, rpe) carry templated test IDs
 * `workout-set-{reps|weight|rpe}-{exerciseIndex}-{setIndex}` so e2e tests
 * can drive any set on any exercise unambiguously.
 */
export default function WorkoutLogger({
  // form state
  title, setTitle,
  duration, setDuration,
  pe, setPe,
  notes, setNotes,
  exercises, setExercises,
  // helpers from parent
  loggerRef,
  exerciseDb,
  activeSuggest, setActiveSuggest,
  suggestionsFor,
  addEx, removeEx, updateEx,
  addSet, updateSet, removeSet,
  onSave,
  // Quick-log: pre-fill the form from the user's last workout.
  lastWorkout, onRepeatLast,
  // Display weight in lbs (input + label) when the user prefers imperial.
  isImperial = false,
  // kg ↔ lbs switcher rendered next to the weight column ("metric"|"imperial").
  onToggleUnits,
  // Coach cue videos: (name) => video | null, and a callback to open the player.
  videoForExercise,
  onPlayVideo,
}) {
  return (
    <div ref={loggerRef} className="obs-card" data-testid="workout-logger">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Sparkles className="h-5 w-5 text-blaze" />
          <div>
            <div className="overline">Log new workout</div>
            <h3 className="font-heading text-2xl uppercase">Log session</h3>
          </div>
        </div>
        {lastWorkout && (
          <button
            type="button"
            data-testid="workout-repeat-last"
            onClick={onRepeatLast}
            className="ghost-btn px-3 py-2 text-xs inline-flex items-center gap-2"
            title={`Pre-fill with ${lastWorkout.title}`}
          >
            <Repeat className="h-3 w-3" />
            Repeat last workout
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Workout title</label>
          <input
            value={title}
            placeholder="e.g. Push Day A"
            onChange={(e) => setTitle(e.target.value)}
            className={`${inputCls} w-full`}
          />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Duration (min)</label>
          <input
            data-testid={WORKOUT.duration}
            type="number"
            value={duration}
            placeholder="60"
            onChange={(e) => setDuration(e.target.value)}
            className={`${inputCls} w-full`}
          />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Perceived effort (1-10)</label>
          <input
            type="number"
            step="0.5"
            min={1}
            max={10}
            value={pe}
            onChange={(e) => setPe(e.target.value)}
            placeholder="7"
            className={`${inputCls} w-full`}
          />
        </div>
      </div>

      <div className="space-y-5">
        {exercises.map((ex, i) => (
          <div key={i} className="border border-obsidian-600 rounded-sm p-4 bg-obsidian-900/40" data-testid={`exercise-card-${i}`}>
            <div className="overline mb-2 flex items-center gap-2">
              Exercise {i + 1}
              {videoForExercise?.(ex.name) && (
                <button
                  type="button"
                  data-testid={`exercise-cue-video-${i}`}
                  onClick={() => onPlayVideo?.(videoForExercise(ex.name))}
                  className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest px-2 py-0.5 border border-blaze/40 text-blaze hover:bg-blaze/10 rounded-sm transition-colors normal-case"
                  title="Watch your coach's cue video"
                >
                  <PlayCircle className="h-3 w-3" /> Coach video
                </button>
              )}
            </div>
            <div className="flex flex-col sm:flex-row sm:items-end gap-3 mb-3">
              <div className="relative flex-1 min-w-0">
                <label className="block text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
                  Exercise name <span className="text-blaze">*</span>
                </label>
                <div className="flex items-center gap-2">
                  <ExerciseIcon key={ex.name} exercise={{ name: ex.name }} size="sm" />
                  <input
                    data-testid={i === 0 ? WORKOUT.exerciseName : `${WORKOUT.exerciseName}-${i}`}
                    value={ex.name}
                    onChange={(e) => { updateEx(i, { name: e.target.value }); setActiveSuggest(i); }}
                    onFocus={() => setActiveSuggest(i)}
                    onBlur={() => setTimeout(() => setActiveSuggest((cur) => (cur === i ? null : cur)), 150)}
                    placeholder="e.g. Bench Press (or type anything)"
                    className={`${inputCls} w-full flex-1 min-w-0`}
                  />
                </div>
                {activeSuggest === i && suggestionsFor(ex.name).length > 0 && (
                  <div className="absolute z-10 left-0 right-0 top-full mt-1 bg-obsidian-800 border border-obsidian-600 rounded-sm shadow-lg max-h-72 overflow-y-auto" data-testid={`ex-suggest-${i}`}>
                    {suggestionsFor(ex.name).map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => { updateEx(i, { name: s.name }); setActiveSuggest(null); }}
                        className="flex items-center gap-3 w-full text-left px-2 py-2 hover:bg-obsidian-700"
                      >
                        <ExerciseIcon exercise={s} size="md" />
                        <div className="flex-1 min-w-0">
                          <div className="font-heading text-sm uppercase truncate">{s.name}</div>
                          <div className="text-[10px] text-zinc-500 uppercase tracking-widest">
                            {s.muscles.slice(0, 2).join(" · ")} · {s.equipment}
                          </div>
                        </div>
                      </button>
                    ))}
                    <Link to="/exercises" className="block px-3 py-2 text-[11px] uppercase tracking-widest text-blaze hover:bg-obsidian-700 border-t border-obsidian-600">
                      <BookOpen className="h-3 w-3 inline mr-2" /> Browse all exercises
                    </Link>
                  </div>
                )}
              </div>
              <div className="flex items-end gap-2 sm:shrink-0">
                <div className="flex-1 sm:flex-none sm:w-28">
                  <label className="block text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Rest (s)</label>
                  <input
                    type="number"
                    value={ex.rest_seconds}
                    onChange={(e) => updateEx(i, { rest_seconds: e.target.value })}
                    placeholder="60"
                    className={`${inputCls} w-full`}
                  />
                </div>
                {exercises.length > 1 && (
                  <button
                    onClick={() => removeEx(i)}
                    className="p-2 text-zinc-500 hover:text-blaze shrink-0"
                    aria-label="Remove exercise"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-12 gap-2 text-[10px] uppercase tracking-widest text-zinc-500 mb-1 items-center">
              <div className="col-span-1">Set</div>
              <div className="col-span-3">Reps</div>
              <div className="col-span-3 flex items-center gap-2 flex-wrap">
                <span>Weight</span>
                {onToggleUnits && (
                  <span className="inline-flex border border-obsidian-600 rounded-sm overflow-hidden" data-testid={`unit-toggle-${i}`}>
                    <button
                      type="button"
                      data-testid={`unit-toggle-kg-${i}`}
                      onClick={() => onToggleUnits("metric")}
                      className={`px-2 py-0.5 text-[9px] uppercase tracking-widest transition-colors ${!isImperial ? "bg-blaze text-white" : "text-zinc-500 hover:text-zinc-200"}`}
                    >
                      kg
                    </button>
                    <button
                      type="button"
                      data-testid={`unit-toggle-lb-${i}`}
                      onClick={() => onToggleUnits("imperial")}
                      className={`px-2 py-0.5 text-[9px] uppercase tracking-widest transition-colors ${isImperial ? "bg-blaze text-white" : "text-zinc-500 hover:text-zinc-200"}`}
                    >
                      lbs
                    </button>
                  </span>
                )}
              </div>
              <div className="col-span-3">RPE (1-10)</div>
              <div className="col-span-2 text-right">Remove</div>
            </div>

            {ex.sets.map((s, j) => (
              <div key={j} className="grid grid-cols-12 gap-2 mb-2" data-testid={`workout-set-row-${i}-${j}`}>
                <div className="col-span-1 grid place-items-center text-zinc-400 text-sm">{j + 1}</div>
                <input
                  data-testid={`workout-set-reps-${i}-${j}`}
                  type="number"
                  value={s.reps}
                  onChange={(e) => updateSet(i, j, { reps: e.target.value })}
                  className={`${inputCls} col-span-3`}
                />
                <NumField
                  data-testid={`workout-set-weight-${i}-${j}`}
                  step="0.5"
                  value={formatWeightInput(s.weight_kg, isImperial)}
                  onCommit={(raw) =>
                    updateSet(i, j, {
                      weight_kg: parseWeightInput(raw, isImperial),
                    })
                  }
                  className={`${inputCls} col-span-3`}
                />
                <input
                  data-testid={`workout-set-rpe-${i}-${j}`}
                  type="number"
                  step="0.5"
                  min={1}
                  max={10}
                  value={s.rpe ?? ""}
                  onChange={(e) => updateSet(i, j, { rpe: e.target.value })}
                  className={`${inputCls} col-span-3`}
                />
                <button
                  data-testid={`workout-set-remove-${i}-${j}`}
                  onClick={() => removeSet(i, j)}
                  className="col-span-2 text-zinc-500 hover:text-blaze grid place-items-center"
                  aria-label="Remove set"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            ))}
            <button
              data-testid={`workout-add-set-${i}`}
              onClick={() => addSet(i)}
              className="ghost-btn px-3 py-1.5 text-[11px] mt-1 inline-flex items-center gap-2"
            >
              <Plus className="h-3 w-3" /> Add set
            </button>
          </div>
        ))}
      </div>

      <div className="mt-4 flex flex-col md:flex-row md:items-end gap-3">
        <div className="flex-1">
          <label className="block text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Session notes</label>
          <textarea
            data-testid={WORKOUT.notes}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="How did it feel?"
            className={`${inputCls} w-full`}
            rows={2}
          />
        </div>
        <div className="flex gap-2">
          <button data-testid={WORKOUT.newBtn} onClick={addEx} className="ghost-btn px-4 py-2.5 text-xs inline-flex items-center gap-2">
            <Plus className="h-3 w-3" /> Exercise
          </button>
          <button data-testid={WORKOUT.saveBtn} onClick={onSave} className="blaze-btn px-5 py-2.5 text-xs inline-flex items-center gap-2">
            <Save className="h-3 w-3" /> Save workout
          </button>
        </div>
      </div>
    </div>
  );
}
